import React, { Fragment, useEffect, useMemo, useRef } from "react";
import { connect } from "react-redux";
import { Breadcrumbs, Grid, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useLocation } from "react-router-dom";
import * as C from "components";
import { setCurrentPage } from "actions";
import BudgetKpiCards from "components/PicTree/Budget/BudgetKpiCards";
import BudgetRemainingTrendChart from "components/PicTree/Budget/BudgetRemainingTrendChart";
import BudgetBySourceTable from "components/PicTree/Budget/BudgetBySourceTable";
import BudgetByBoostTable from "components/PicTree/Budget/BudgetByBoostTable";
import CalendarRangePicker from "components/Calendar/CalendarRangePicker";
import CalendarRangePicTreeNtt from "components/Calendar/CalendarRangePicTreeNtt";
import CalendarRangePicTreeNttFt from "components/Calendar/CalendarRangePicTreeNttFt";
import CalendarRangePicTreeHokuden from "components/Calendar/CalendarRangePicTreeHokuden";
import { getBudgetRemainTrendData, getBudgetSummaryData } from "actions";

const useStyles = makeStyles((theme) => ({
  breadcrumbs: {
    marginTop: 16,
    minHeight: 30,
  },
  sectionGap: {
    marginTop: theme.spacing(2),
  },
}));

const formatBudgetValue = (amount) => {
  const numericAmount = Number(amount || 0);
  if (!Number.isFinite(numericAmount)) {
    return "¥--";
  }

  if (Math.abs(numericAmount) < 10000000) {
    return `¥${numericAmount.toLocaleString("ja-JP")}`;
  }

  return `¥${(numericAmount / 1000000).toFixed(1)}M`;
};

const formatBudgetSummaryValue = (summary, key) => {
  if (!summary || summary[key] === undefined || summary[key] === null) {
    return "¥--";
  }

  return formatBudgetValue(summary[key]);
};

const formatBudgetRate = (rate) => {
  if (rate === null || rate === undefined || Number.isNaN(Number(rate))) {
    return "--%";
  }
  return `${Number(rate).toFixed(1)}%`;
};

const formatBudgetCount = (count) => {
  if (count === null || count === undefined || Number.isNaN(Number(count))) {
    return "--";
  }

  return String(Number(count));
};

const resolveConsumedRate = (summary) => {
  if (!summary) {
    return null;
  }

  const totalBudgetAmount = Number(summary.totalBudgetAmount || 0);
  const consumedBudgetAmount = Number(summary.consumedBudgetAmount || 0);

  if (totalBudgetAmount > 0 && Number.isFinite(consumedBudgetAmount)) {
    return Number(((consumedBudgetAmount / totalBudgetAmount) * 100).toFixed(1));
  }

  return summary.consumedRate ?? null;
};

const buildBudgetBySourceRowsFromSummary = (company, summary) => {
  if (!summary) {
    return [];
  }

  const totalBudgetAmount = Number(summary.totalBudgetAmount || 0);
  const consumedBudgetAmount = Number(summary.consumedBudgetAmount || 0);
  const remainingBudgetAmount = Number(summary.remainingBudgetAmount || 0);
  const progress = totalBudgetAmount > 0
    ? Number(((consumedBudgetAmount / totalBudgetAmount) * 100).toFixed(1))
    : 0;

  const code = company === "ntt" ? "RS" : company.toUpperCase();
  const name = company === "ntt" ? "レギュラーS" : companyLabelForBudgetBySource(company);

  return [{
    code,
    name,
    budget: formatBudgetValue(totalBudgetAmount),
    used: formatBudgetValue(consumedBudgetAmount),
    remaining: formatBudgetValue(remainingBudgetAmount),
    progress,
    progressLabel: `${progress.toFixed(1)}%`,
    color: "#3F7ECC",
  }];
};

const companyLabelForBudgetBySource = (company) => {
  if (company === "ntt-ft") return "NTT FT";
  if (company === "hokuden") return "HOKUDEN";
  if (company === "tepco") return "TEPCO";
  return "NTT-ME";
};

const PicTreeReviewBudget = ({
  setCurrentPage,
  pageTopLoader,
  budgetSummaryTepco,
  budgetSummaryNtt,
  budgetSummaryNttFt,
  budgetSummaryHokuden,
  budgetBoostRowsTepco,
  budgetBoostRowsNtt,
  budgetBoostRowsNttFt,
  budgetBoostRowsHokuden,
  budgetRemainingTrendTepco,
  budgetRemainingTrendNtt,
  budgetRemainingTrendNttFt,
  budgetRemainingTrendHokuden,
  selectedSeasonTepco,
  selectedSeasonNtt,
  selectedSeasonNttFt,
  selectedSeasonHokuden,
  dateRangePicTree,
  dateRangePicTreeNtt,
  dateRangePicTreeNttFt,
  dateRangePicTreeHokuden,
  getBudgetRemainTrendData,
  getBudgetSummaryData,
}) => {
  const classes = useStyles();
  const location = useLocation();
  const pathname = location.pathname;
  const company = pathname.split("/")[3] || "tepco";
  const hasInitialBudgetFetchRef = useRef(false);

  const companyLabel = (() => {
    if (company === "ntt") return "PicTrée NTT ME";
    if (company === "ntt-ft") return "PicTrée NTT FT";
    if (company === "hokuden") return "PicTrée HOKUDEN";
    return "PicTrée TEPCO";
  })();

  const budgetSummary = (() => {
    if (company === "ntt") return budgetSummaryNtt;
    if (company === "ntt-ft") return budgetSummaryNttFt;
    if (company === "hokuden") return budgetSummaryHokuden;
    return budgetSummaryTepco;
  })();
  const budgetRemainTrend = (() => {
    if (company === "ntt") return budgetRemainingTrendNtt;
    if (company === "ntt-ft") return budgetRemainingTrendNttFt;
    if (company === "hokuden") return budgetRemainingTrendHokuden;
    return budgetRemainingTrendTepco;
  })();
  const budgetBoostRows = (() => {
    if (company === "ntt") return budgetBoostRowsNtt;
    if (company === "ntt-ft") return budgetBoostRowsNttFt;
    if (company === "hokuden") return budgetBoostRowsHokuden;
    return budgetBoostRowsTepco;
  })();
  const budgetBySourceRows = useMemo(
    () => buildBudgetBySourceRowsFromSummary(company, budgetSummary),
    [company, budgetSummary]
  );
  const isBudgetLoading = Boolean(pageTopLoader);

  const renderCalendar = () => {
    if (company === "ntt") {
      return <CalendarRangePicTreeNtt mDate={"2024/12/07"} page="pictree" pictree />;
    }
    if (company === "ntt-ft") {
      return <CalendarRangePicTreeNttFt mDate={"2026/01/31"} page="pictree" pictree />;
    }
    if (company === "hokuden") {
      return <CalendarRangePicTreeHokuden mDate={"2025/07/12"} page="pictree" pictree />;
    }
    return <CalendarRangePicker mDate={"2024/04/13"} page="pictree" pictree />;
  };

  useEffect(() => {
    hasInitialBudgetFetchRef.current = false;
    setCurrentPage(pathname);
  }, [pathname, setCurrentPage]);

  useEffect(() => {
    if (hasInitialBudgetFetchRef.current) {
      return;
    }

    if (company === "ntt") {
      if (
        Array.isArray(selectedSeasonNtt) &&
        selectedSeasonNtt.length > 0 &&
        Array.isArray(dateRangePicTreeNtt) &&
        dateRangePicTreeNtt[0] &&
        dateRangePicTreeNtt[1]
      ) {
        getBudgetSummaryData({ companyCode: "ntt" });
        getBudgetRemainTrendData({ companyCode: "ntt" });
        hasInitialBudgetFetchRef.current = true;
      }
      return;
    }

    if (company === "ntt-ft") {
      if (
        Array.isArray(selectedSeasonNttFt) &&
        selectedSeasonNttFt.length > 0 &&
        Array.isArray(dateRangePicTreeNttFt) &&
        dateRangePicTreeNttFt[0] &&
        dateRangePicTreeNttFt[1]
      ) {
        getBudgetSummaryData({ companyCode: "ntt-ft" });
        getBudgetRemainTrendData({ companyCode: "ntt-ft" });
        hasInitialBudgetFetchRef.current = true;
      }
      return;
    }

    if (company === "hokuden") {
      if (
        Array.isArray(selectedSeasonHokuden) &&
        selectedSeasonHokuden.length > 0 &&
        Array.isArray(dateRangePicTreeHokuden) &&
        dateRangePicTreeHokuden[0] &&
        dateRangePicTreeHokuden[1]
      ) {
        getBudgetSummaryData({ companyCode: "hokuden" });
        getBudgetRemainTrendData({ companyCode: "hokuden" });
        hasInitialBudgetFetchRef.current = true;
      }
      return;
    }

    if (
      Array.isArray(selectedSeasonTepco) &&
      selectedSeasonTepco.length > 0 &&
      Array.isArray(dateRangePicTree) &&
      dateRangePicTree[0] &&
      dateRangePicTree[1]
    ) {
      getBudgetSummaryData({ companyCode: "tepco" });
      getBudgetRemainTrendData({ companyCode: "tepco" });
      hasInitialBudgetFetchRef.current = true;
    }
  }, [
    pathname,
    company,
    selectedSeasonTepco,
    selectedSeasonNtt,
    selectedSeasonNttFt,
    selectedSeasonHokuden,
    dateRangePicTree,
    dateRangePicTreeNtt,
    dateRangePicTreeNttFt,
    dateRangePicTreeHokuden,
    getBudgetRemainTrendData,
    getBudgetSummaryData,
  ]);

  const viewModel = useMemo(
    () => ({
      cards: [
        {
          key: "total",
          title: "総予算",
          value: isBudgetLoading ? "¥--" : formatBudgetSummaryValue(budgetSummary, "totalBudgetAmount"),
          subtitle: `全原資`,
        },
        {
          key: "used",
          title: "消化済み",
          value: isBudgetLoading ? "¥--" : formatBudgetSummaryValue(budgetSummary, "consumedBudgetAmount"),
          subtitle: "",
          subtitleHighlight: `消化率 ${isBudgetLoading ? "--%" : budgetSummary ? formatBudgetRate(resolveConsumedRate(budgetSummary)) : "--%"}`,
        },
        {
          key: "remaining",
          title: "残予算",
          value: isBudgetLoading ? "¥--" : formatBudgetSummaryValue(budgetSummary, "remainingBudgetAmount"),
          subtitle: "全原資合計",
        },
        {
          key: "boostCount",
          title: "ブーストイベント数",
          value: isBudgetLoading ? "--" : formatBudgetCount(budgetSummary?.boostEventCount),
          subtitle: "実施済み",
        },
      ],
      trend: budgetRemainTrend?.trend || { date: [] },
      trendLegend: budgetRemainTrend?.legend || [],
      trendChartConfig: budgetRemainTrend?.chartConfig || {},
      trendColorValue: budgetRemainTrend?.colorValue || [],
      bySourceRows: budgetBySourceRows,
      byBoostRows: budgetBoostRows || [],
    }),
    [budgetSummary, budgetRemainTrend, budgetBySourceRows, budgetBoostRows, isBudgetLoading]
  );

  return (
    <Fragment>
      <C.ContentWrapper>
        <Grid container spacing={0} direction="row" justifyContent="space-between" style={{ marginTop: -16, marginBottom: 8 }}>
          <Grid item>
            <Breadcrumbs separator="›" className={classes.breadcrumbs}>
              <Typography>{companyLabel}</Typography>
              <Typography variant="body1">撮影</Typography>
              <Typography variant="body1">バジェット</Typography>
            </Breadcrumbs>
          </Grid>
          <Grid item className={classes.fillRemailSpace}></Grid>
          <Grid item>
            {renderCalendar()}
          </Grid>
        </Grid>

        <BudgetKpiCards cards={viewModel.cards} />

        <Grid container className={classes.sectionGap}>
          <Grid item xs={12}>
            <BudgetRemainingTrendChart
              trend={viewModel.trend}
              legend={viewModel.trendLegend}
              chartConfig={viewModel.trendChartConfig}
              colorValue={viewModel.trendColorValue}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} className={classes.sectionGap}>
          <Grid item xs={12} md={6}>
            <BudgetBySourceTable rows={viewModel.bySourceRows} />
          </Grid>
          <Grid item xs={12} md={6}>
            <BudgetByBoostTable rows={viewModel.byBoostRows} />
          </Grid>
        </Grid>
      </C.ContentWrapper>
    </Fragment>
  );
};

const mapDispatchToProps = (dispatch) => ({
  setCurrentPage: (param) => dispatch(setCurrentPage(param)),
  getBudgetSummaryData: (param) => dispatch(getBudgetSummaryData(param)),
  getBudgetRemainTrendData: (param) => dispatch(getBudgetRemainTrendData(param)),
});

const mapStateToProps = (state) => ({
  budgetSummaryTepco: state.pictree.budgetSummary,
  budgetSummaryNtt: state.pictreeNtt.budgetSummary,
  budgetSummaryNttFt: state.pictreeNttFt.budgetSummary,
  budgetSummaryHokuden: state.pictreeHokuden.budgetSummary,
  budgetRemainingTrendTepco: state.pictree.budgetRemainingTrend,
  budgetRemainingTrendNtt: state.pictreeNtt.budgetRemainingTrend,
  budgetRemainingTrendNttFt: state.pictreeNttFt.budgetRemainingTrend,
  budgetRemainingTrendHokuden: state.pictreeHokuden.budgetRemainingTrend,
  budgetBoostRowsTepco: state.pictree.budgetByBoostRows,
  budgetBoostRowsNtt: state.pictreeNtt.budgetByBoostRows,
  budgetBoostRowsNttFt: state.pictreeNttFt.budgetByBoostRows,
  budgetBoostRowsHokuden: state.pictreeHokuden.budgetByBoostRows,
  selectedSeasonTepco: state.pictree.selectedSeason,
  selectedSeasonNtt: state.pictreeNtt.selectedSeason,
  selectedSeasonNttFt: state.pictreeNttFt.selectedSeason,
  selectedSeasonHokuden: state.pictreeHokuden.selectedSeason,
  dateRangePicTree: state.page.dateRangePicTree,
  dateRangePicTreeNtt: state.page.dateRangePicTreeNtt,
  dateRangePicTreeNttFt: state.page.dateRangePicTreeNttFt,
  dateRangePicTreeHokuden: state.page.dateRangePicTreeHokuden,
  pageTopLoader: state.page.pageTopLoader,
});

export default connect(mapStateToProps, mapDispatchToProps)(PicTreeReviewBudget);
